import { useMemo } from "react";
import JSZip from "jszip";
import { usePropertyReportPhotos } from "@/features/properties/hooks/usePropertyReportPhotos";
import type { AssetSpecs, ReportAsset } from "@/features/properties/hooks/usePropertyReportPhotos";
import { useDownloadManager, type BuildContext } from "@/features/exports/services/DownloadManagerContext";
import { buildPropertyPhotosBundle } from "@/features/exports/services/bundles";
import { sanitize } from "@/features/exports/services/_helpers";
import {
  PropertyReportGalleryView,
  type ReportGalleryItem,
} from "@/features/properties/components/PropertyReportGalleryView";

async function buildCategoryBundleFromMemory(
  assetsInCategory: ReportGalleryItem[],
  categoryLabel: string,
  ctx: BuildContext,
): Promise<Blob> {
  const photos = assetsInCategory.flatMap((a) =>
    a.photos.map((p) => ({ photo: p, assetName: a.name })),
  );
  if (photos.length === 0) {
    throw new Error("No photos in this category.");
  }
  const zip = new JSZip();
  let processed = 0;
  const usedNames = new Set<string>();
  const folder = sanitize(categoryLabel);
  ctx.setProgress(2, "Starting download...");
  for (const { photo, assetName } of photos) {
    if (ctx.signal.aborted) throw new Error("Cancelled");
    try {
      const res = await fetch(photo.url, { signal: ctx.signal });
      if (res.ok) {
        const blob = await res.blob();
        const assetFolder = sanitize(assetName);
        let name = photo.document_name || `${photo.id}.jpg`;
        const key = `${folder}/${assetFolder}/${name}`;
        if (usedNames.has(key)) {
          const dot = name.lastIndexOf(".");
          name = dot > 0
            ? `${name.slice(0, dot)}_${processed + 1}${name.slice(dot)}`
            : `${name}_${processed + 1}`;
        }
        const finalKey = `${folder}/${assetFolder}/${name}`;
        usedNames.add(finalKey);
        zip.file(finalKey, blob);
      }
    } catch (err) {
      if (ctx.signal.aborted) throw err;
      console.warn(`Skipping photo ${photo.id} in category zip:`, err);
    }
    processed++;
    ctx.setProgress(
      2 + Math.floor((processed / photos.length) * 94),
      `${processed} / ${photos.length} photos`,
    );
  }
  ctx.setProgress(97, "Compressing...");
  const blob = await zip.generateAsync({ type: "blob", compression: "STORE" });
  ctx.setProgress(100, "Done");
  return blob;
}

const SPEC_FIELDS: ReadonlyArray<{ key: keyof AssetSpecs; label: string }> = [
  { key: "asset_type", label: "Equipment Type" },
  { key: "manufacturer", label: "Manufacturer" },
  { key: "model_name", label: "Model Name" },
  { key: "model_number", label: "Model Number" },
  { key: "serial_number", label: "Serial Number" },
  { key: "description", label: "Description" },
  { key: "manufacture_date", label: "Manufacture Date" },
  { key: "estimated_age_years", label: "Estimated Age (Years)" },
  { key: "age_source", label: "Age Source" },
  { key: "amperage", label: "Amps" },
  { key: "voltage", label: "Volts" },
  { key: "phase", label: "Phase" },
  { key: "wires", label: "Wires" },
  { key: "btu_capacity", label: "BTU Capacity" },
  { key: "capacity_gallons", label: "Capacity (gal)" },
  { key: "pressure_psi", label: "Pressure (PSI)" },
  { key: "frequency_hz", label: "Frequency (Hz)" },
  { key: "power_kw", label: "Power (kW)" },
  { key: "efficiency_rating", label: "Efficiency" },
  { key: "fuel_type", label: "Fuel Type" },
  { key: "airflow_cfm", label: "Airflow (CFM)" },
  { key: "gas_cfh_low_diff", label: 'Gas CFH (1/2")' },
  { key: "gas_cfh_high_diff", label: 'Gas CFH (2")' },
  { key: "condition_status", label: "Condition" },
  { key: "warranty_expires", label: "Warranty Expires" },
];

function formatSpecValue(key: keyof AssetSpecs, value: string | number): string {
  if ((key === "warranty_expires" || key === "manufacture_date") && typeof value === "string") {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString();
    }
  }
  return String(value);
}

function toGalleryItem(asset: ReportAsset): ReportGalleryItem {
  return {
    id: asset.id,
    name: asset.name,
    category: asset.category,
    photos: asset.photos,
    details: SPEC_FIELDS.flatMap(({ key, label }) => {
      const value = asset.specs[key];
      if (value === null || value === undefined || value === "") return [];
      return [{ key: String(key), label, value: formatSpecValue(key, value) }];
    }),
  };
}

interface PropertyReportGalleryProps {
  clientId: string;
  propertyId: string;
  propertyName: string;
}

export function PropertyReportGallery({ clientId, propertyId, propertyName }: PropertyReportGalleryProps) {
  const { assets, loading, error, refetch } = usePropertyReportPhotos(propertyId);
  const { enqueue } = useDownloadManager();

  const items = useMemo(() => assets.map(toGalleryItem), [assets]);
  const totalPhotoCount = useMemo(
    () => items.reduce((n, item) => n + item.photos.length, 0),
    [items],
  );
  const slug = sanitize(propertyName || "property");

  const handleDownloadAll = () => {
    if (totalPhotoCount === 0) return;
    enqueue({
      label: `Photo bundle - ${propertyName || "Property"}`,
      filename: `${slug}_photos.zip`,
      build: (ctx) =>
        buildPropertyPhotosBundle({ projectId: clientId, propertyId }, ctx),
    });
  };

  const handleDownloadCategory = (category: string, itemsInCategory: ReportGalleryItem[]) => {
    enqueue({
      label: `${category} photos - ${propertyName || "Property"}`,
      filename: `${slug}_${sanitize(category)}.zip`,
      build: (ctx) => buildCategoryBundleFromMemory(itemsInCategory, category, ctx),
    });
  };

  return (
    <PropertyReportGalleryView
      items={items}
      loading={loading}
      error={error}
      onRetry={refetch}
      searchPlaceholder="Search equipment..."
      emptyNavMessage="No photos attached to any asset yet."
      detailTitle="Technical Specifications"
      detailEmptyMessage="Select an asset to view specifications."
      downloadAllLabel="Download all photos"
      onDownloadAll={handleDownloadAll}
      onDownloadCategory={handleDownloadCategory}
    />
  );
}
